/**
 * Global fetch interceptor — detects 401 Unauthorized from our backend and
 * logs the user out cleanly instead of leaving stale UI on screen.
 *
 * Problem: when the JWT expires after long inactivity, every API call starts
 * returning 401, but the page still shows old content. Users see a "broken"
 * app until they hard-refresh. This wrapper fixes that.
 */
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let installed = false;
let redirecting = false;

export function installAuthFetchInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    try {
      if (redirecting) return response;

      const url = typeof input === 'string' ? input : (input?.url || '');
      // Accept both absolute (API_URL/api/...) and relative (/api/...) calls
      const isOurBackend =
        url.includes('/api/') &&
        (
          (API_URL && url.startsWith(API_URL)) ||
          url.startsWith('/api/') ||
          url.startsWith(window.location.origin + '/api/')
        );

      // Skip the login endpoint itself — a 401 there means "wrong password",
      // which should NOT trigger a global logout/redirect.
      const isLoginCall = url.includes('/api/auth/login');
      const isRegisterCall = url.includes('/api/auth/register');
      const isSetupCall = url.includes('/api/setup/');

      if (
        response.status === 401 &&
        isOurBackend &&
        !isLoginCall &&
        !isRegisterCall &&
        !isSetupCall &&
        localStorage.getItem('token')
      ) {
        redirecting = true;
        // Clear client-side auth state
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        toast.error('Your session has expired. Please sign in again.', {
          id: 'session-expired',
          duration: 4000,
        });

        // Give the toast a moment, then hard-navigate to /auth so every
        // in-flight stale API call is abandoned and the UI resets cleanly.
        setTimeout(() => {
          const redirectPath = '/auth';
          if (window.location.pathname !== redirectPath) {
            window.location.assign(redirectPath);
          } else {
            window.location.reload();
          }
        }, 500);
      }
    } catch (err) {
      // Never let the interceptor break the original fetch
      // eslint-disable-next-line no-console
      console.warn('[auth-interceptor] non-fatal:', err);
    }

    return response;
  };
}
